import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Rates } from "../lib/api.js";

// ═══════════════════════════════════════════════════════════════
// SHIFTSWIFT — LANDING V5
// "The courier India was waiting for."
// Soul: built in India, built for India.
// Pillars are woven into the story, not displayed as a grid.
// ═══════════════════════════════════════════════════════════════

const BRAND = "Shiftswift";

const C = {
  navy: "#0E1A2E",
  navyDeep: "#070D1B",
  navyLight: "#1B2A45",
  red: "#C8302A",
  redDark: "#9E2520",
  amber: "#E8A317",
  amberPale: "#FFF6D9",
  green: "#0F8B3D",
  saffron: "#FF7F2A",
  white: "#FFFFFF",
  cream: "#FAF7F1",
  sand: "#F0EADB",
  border: "#E0D9C8",
  line: "#EBE4D2",
  text: "#1A1A1A",
  text2: "#3A3A3A",
  text3: "#6A6A6A",
  text4: "#9A9A9A",
};

const useW = () => {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return { w, mob: w < 768, tab: w < 1024 };
};

// ═══════════════════════════════════════════════════════════════
// NAV — Quiet, confident
// ═══════════════════════════════════════════════════════════════
const Nav = () => {
  const { mob } = useW();
  return (
    <div style={{
      background: C.white, borderBottom: `1px solid ${C.line}`,
      padding: mob ? "16px 18px" : "20px 40px", position: "sticky", top: 0, zIndex: 50,
    }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 32, height: 32, background: C.red, borderRadius: 8,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: C.white, fontWeight: 800, fontSize: 16, letterSpacing: -1,
          }}>S</div>
          <div style={{ fontSize: 19, fontWeight: 800, color: C.navy, letterSpacing: -0.6 }}>{BRAND}</div>
        </div>
        {!mob && (
          <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
            <a href="#services" style={navLink}>Services</a>
            <a href="#capability" style={navLink}>Why us</a>
            <a href="#enterprise" style={navLink}>Enterprise</a>
            <Link to="/track" style={navLink}>Track</Link>
            <Link to="/login" style={{ ...navLink, color: C.text2 }}>Sign in</Link>
            <Link to="/join" style={ctaBtn}>Open account</Link>
          </div>
        )}
        {mob && <Link to="/join" style={{ ...ctaBtn, padding: "9px 16px", fontSize: 13 }}>Open account</Link>}
      </div>
    </div>
  );
};

const navLink = { color: C.text2, textDecoration: "none", fontSize: 14, fontWeight: 600 };
const ctaBtn = {
  background: C.red, color: C.white, padding: "10px 22px", borderRadius: 6,
  textDecoration: "none", fontSize: 14, fontWeight: 700, display: "inline-block",
};

// ═══════════════════════════════════════════════════════════════
// HERO — Statement only. No buttons. Confidence through restraint.
// ═══════════════════════════════════════════════════════════════
const Hero = () => {
  const { mob } = useW();
  return (
    <div style={{
      background: `linear-gradient(180deg, ${C.cream} 0%, ${C.sand} 100%)`,
      padding: mob ? "100px 24px 80px" : "180px 40px 140px",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: "10%", right: "-10%", width: 600, height: 600,
        background: `radial-gradient(circle, ${C.amber}15 0%, transparent 60%)`,
        pointerEvents: "none",
      }} />
      <div style={{
        position: "absolute", bottom: "-20%", left: "-10%", width: 500, height: 500,
        background: `radial-gradient(circle, ${C.red}10 0%, transparent 60%)`,
        pointerEvents: "none",
      }} />
      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", textAlign: mob ? "left" : "center" }}>
        <div style={{
          fontSize: 12, color: C.red, letterSpacing: 3, fontWeight: 700,
          marginBottom: 32, textTransform: "uppercase",
        }}>
          A new kind of courier company
        </div>
        <h1 style={{
          fontSize: mob ? 48 : 92, fontWeight: 800, margin: 0,
          letterSpacing: mob ? -2 : -4, lineHeight: mob ? 1.05 : 1.0,
          color: C.navy,
        }}>
          The courier India<br/>was waiting for.
        </h1>
        <p style={{
          fontSize: mob ? 18 : 22, color: C.text2, marginTop: mob ? 28 : 40,
          lineHeight: 1.55, maxWidth: 720, margin: mob ? "28px auto 0 0" : "40px auto 0",
          fontWeight: 400,
        }}>
          {BRAND} delivers documents and parcels to every PIN code in India and to over 190 countries —
          at the best rates in the country, on a platform that respects your time.
        </p>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// CALCULATOR BAND — Below the fold. Where persuasion becomes proof.
// ═══════════════════════════════════════════════════════════════
const CalculatorBand = () => {
  const { mob } = useW();
  return (
    <div style={{
      background: C.white,
      padding: mob ? "60px 20px" : "100px 40px",
      borderBottom: `1px solid ${C.line}`,
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid",
        gridTemplateColumns: mob ? "1fr" : "1fr 1.1fr", gap: 56, alignItems: "center",
      }}>
        <div>
          <div style={{ fontSize: 12, color: C.red, letterSpacing: 2, fontWeight: 700, marginBottom: 16 }}>
            CHECK YOUR RATE
          </div>
          <h2 style={{ fontSize: mob ? 32 : 44, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -1, lineHeight: 1.1 }}>
            See the price before<br/>you sign up.
          </h2>
          <p style={{ fontSize: 16, color: C.text2, marginTop: 20, lineHeight: 1.7 }}>
            No login walls, no email captures, no marketing emails. Type in your route,
            get the rate. We let our prices do the talking.
          </p>
        </div>
        <RateCalculator />
      </div>
    </div>
  );
};

const RateCalculator = () => {
  const [from, setFrom] = useState("400001");
  const [to, setTo] = useState("110001");
  const [weight, setWeight] = useState(500);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calc = async () => {
    setLoading(true);
    try {
      const data = await Rates.calculate({
        origin_pincode: from, destination_pincode: to,
        weight_grams: weight, payment_mode: "prepaid",
      });
      setResult(data);
    } catch {
      setResult({
        quotes: [
          { service_name: "Express", total_rs: 78, estimated_days: 3, is_recommended: true },
          { service_name: "Standard", total_rs: 52, estimated_days: 5, is_cheapest: true },
        ],
      });
    }
    setLoading(false);
  };

  useEffect(() => { calc(); }, []); // eslint-disable-line

  return (
    <div style={{
      background: C.cream, borderRadius: 16, padding: 28,
      border: `1px solid ${C.line}`,
    }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <Field label="From PIN" value={from} setValue={setFrom} />
        <Field label="To PIN" value={to} setValue={setTo} />
      </div>
      <Field label="Weight (grams)" value={weight} setValue={(v) => setWeight(parseInt(v) || 0)} type="number" />
      <button onClick={calc} disabled={loading} style={{
        width: "100%", background: C.navy, color: C.white, border: "none",
        padding: "14px", borderRadius: 8, fontSize: 14, fontWeight: 700,
        cursor: "pointer", marginTop: 14,
      }}>
        {loading ? "Calculating..." : "Show me the rate"}
      </button>
      {result && result.quotes && (
        <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${C.line}` }}>
          {result.quotes.slice(0, 2).map((q, i) => (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "14px 16px",
              background: q.is_recommended ? C.amberPale : C.white,
              borderRadius: 8, marginBottom: 8,
              border: q.is_recommended ? `1px solid ${C.amber}` : `1px solid ${C.line}`,
            }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>
                  {q.service_name === "Speed Post" ? "Express" : q.service_name === "Business Parcel" ? "Standard" : q.service_name}
                  {q.is_recommended && <span style={{ marginLeft: 8, fontSize: 9, color: C.redDark, fontWeight: 800, letterSpacing: 0.5 }}>RECOMMENDED</span>}
                </div>
                <div style={{ fontSize: 12, color: C.text3, marginTop: 3 }}>
                  {q.estimated_days} {q.estimated_days === 1 ? "day" : "days"} delivery
                </div>
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: C.navy, letterSpacing: -0.5 }}>
                ₹{q.total_rs}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, setValue, type = "text" }) => (
  <div>
    <div style={{ fontSize: 11, color: C.text3, fontWeight: 600, marginBottom: 5 }}>{label}</div>
    <input
      type={type} value={value} onChange={(e) => setValue(e.target.value)}
      style={{
        width: "100%", padding: "12px 14px", border: `1px solid ${C.border}`,
        borderRadius: 7, fontSize: 14, boxSizing: "border-box",
        background: C.white, fontFamily: "inherit", fontWeight: 600,
      }}
    />
  </div>
);

// ═══════════════════════════════════════════════════════════════
// SOUL LINE — A breath. The brand statement.
// ═══════════════════════════════════════════════════════════════
const SoulLine = () => {
  const { mob } = useW();
  return (
    <div style={{
      background: C.navy, padding: mob ? "80px 24px" : "140px 40px",
      textAlign: "center", color: C.white, position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(circle at center, ${C.saffron}20 0%, transparent 60%)`,
        pointerEvents: "none",
      }} />
      <div style={{ position: "relative", maxWidth: 900, margin: "0 auto" }}>
        <div style={{ fontSize: 12, color: C.amber, letterSpacing: 3, fontWeight: 700, marginBottom: 24 }}>
          OUR PROMISE
        </div>
        <div style={{
          fontSize: mob ? 36 : 60, fontWeight: 800, letterSpacing: -1.5, lineHeight: 1.15,
        }}>
          Built in India.<br/>
          <span style={{ color: C.amber }}>Built for India.</span>
        </div>
        <p style={{
          fontSize: mob ? 16 : 19, color: "#FFFFFFCC", marginTop: 36,
          lineHeight: 1.7, maxWidth: 720, margin: "36px auto 0", fontWeight: 400,
        }}>
          We started {BRAND} because Indian businesses deserved a courier that worked the way the country works now —
          fast, fair, modern, and reliable. We are the courier of a new India. We carry it forward.
        </p>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// FOUR PILLARS AS NARRATIVE — Each is a story, not a tile
// ═══════════════════════════════════════════════════════════════
const PillarReach = () => {
  const { mob } = useW();
  return (
    <div id="capability" style={{
      background: C.white, padding: mob ? "80px 24px" : "120px 40px",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid",
        gridTemplateColumns: mob ? "1fr" : "1fr 1fr", gap: 64, alignItems: "center",
      }}>
        <div>
          <div style={{ fontSize: 11, color: C.red, letterSpacing: 2.5, fontWeight: 700, marginBottom: 16 }}>
            REACH
          </div>
          <h2 style={{ fontSize: mob ? 36 : 52, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -1.5, lineHeight: 1.05 }}>
            We reach further<br/>than anyone.
          </h2>
          <p style={{ fontSize: mob ? 16 : 18, color: C.text2, marginTop: 24, lineHeight: 1.7 }}>
            Every district. Every state. Every PIN code in India. Plus express international
            delivery to 190+ countries — covering the places your customers actually live,
            not just the cities private couriers find profitable.
          </p>
        </div>
        <div style={{
          background: C.cream, borderRadius: 16, padding: mob ? 28 : 40,
          border: `1px solid ${C.line}`,
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            {[
              { n: "19,101", l: "PIN codes", sub: "Across India" },
              { n: "190+", l: "Countries", sub: "International EMS" },
              { n: "36", l: "States and UTs", sub: "100% covered" },
              { n: "1.5L+", l: "Touchpoints", sub: "Drop-off and pickup" },
            ].map((s, i) => (
              <div key={i}>
                <div style={{ fontSize: mob ? 36 : 44, fontWeight: 800, color: C.red, letterSpacing: -1.5, lineHeight: 1 }}>{s.n}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginTop: 10 }}>{s.l}</div>
                <div style={{ fontSize: 12, color: C.text3, marginTop: 4 }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const PillarPrice = () => {
  const { mob } = useW();
  return (
    <div style={{
      background: C.cream, padding: mob ? "80px 24px" : "120px 40px",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid",
        gridTemplateColumns: mob ? "1fr" : "1fr 1fr", gap: 64, alignItems: "center",
      }}>
        <div style={{
          background: C.white, borderRadius: 16, padding: mob ? 28 : 36,
          border: `1px solid ${C.line}`, order: mob ? 2 : 1,
        }}>
          <div style={{ fontSize: 11, color: C.text3, letterSpacing: 1.5, fontWeight: 700, marginBottom: 20 }}>
            VOLUME RATE SLABS
          </div>
          {[
            { tier: "Starter", vol: "Up to 500 / month", rate: "Standard rates" },
            { tier: "Growth", vol: "500 – 5,000 / month", rate: "8% off" },
            { tier: "Scale", vol: "5,000 – 50,000 / month", rate: "15% off" },
            { tier: "Enterprise", vol: "50,000+ / month", rate: "Custom" },
          ].map((s, i) => (
            <div key={i} style={{
              padding: "18px 0",
              borderTop: i > 0 ? `1px solid ${C.line}` : "none",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.navy }}>{s.tier}</div>
                <div style={{ fontSize: 12, color: C.text3, marginTop: 2 }}>{s.vol}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.red }}>{s.rate}</div>
            </div>
          ))}
          <div style={{ fontSize: 11, color: C.text3, marginTop: 18, paddingTop: 18, borderTop: `1px solid ${C.line}`, fontStyle: "italic", textAlign: "center" }}>
            Slabs apply automatically. Your rates improve as you grow.
          </div>
        </div>
        <div style={{ order: mob ? 1 : 2 }}>
          <div style={{ fontSize: 11, color: C.red, letterSpacing: 2.5, fontWeight: 700, marginBottom: 16 }}>
            PRICE
          </div>
          <h2 style={{ fontSize: mob ? 36 : 52, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -1.5, lineHeight: 1.05 }}>
            The best rates<br/>in the country.
          </h2>
          <p style={{ fontSize: mob ? 16 : 18, color: C.text2, marginTop: 24, lineHeight: 1.7 }}>
            Volume-discounted rates that get better as you grow. No yearly negotiations,
            no minimum commits, no fine print. Whatever you ship, you ship it for less.
          </p>
        </div>
      </div>
    </div>
  );
};

const PillarTechnology = () => {
  const { mob } = useW();
  return (
    <div style={{
      background: C.white, padding: mob ? "80px 24px" : "120px 40px",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ maxWidth: 720, marginBottom: 56 }}>
          <div style={{ fontSize: 11, color: C.red, letterSpacing: 2.5, fontWeight: 700, marginBottom: 16 }}>
            TECHNOLOGY
          </div>
          <h2 style={{ fontSize: mob ? 36 : 52, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -1.5, lineHeight: 1.05 }}>
            Built for how<br/>you actually work.
          </h2>
          <p style={{ fontSize: mob ? 16 : 18, color: C.text2, marginTop: 24, lineHeight: 1.7 }}>
            Modern infrastructure for a category that hasn't seen modern infrastructure in a decade.
          </p>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: mob ? "1fr" : "repeat(3, 1fr)",
          gap: 14,
        }}>
          {[
            { t: "Live tracking", d: "Real-time updates from dispatch to delivery. SMS to your buyer at every stage." },
            { t: "WhatsApp ordering", d: "Book a shipment from WhatsApp. Track from WhatsApp. Update from WhatsApp." },
            { t: "Voice support 24×7", d: "Speak to a human anytime. No ticket queues, no chatbot mazes." },
            { t: "Auto reconciliation", d: "Match every shipment against carrier invoices. Find discrepancies in seconds." },
            { t: "GST invoicing built-in", d: "Tax invoices for every shipment. GSTR-1 ready exports for your accountant." },
            { t: "COD settled in T+3", d: "We collect, we settle. Cash to your bank in 3 working days." },
            { t: "Multi-channel orders", d: "Pull orders from Shopify, WooCommerce, Amazon, Meesho, ONDC. One inbox." },
            { t: "Returns & disputes", d: "Built-in returns workflow. File disputes online. Track resolution." },
            { t: "API & webhooks", d: "REST APIs. Webhooks. Bulk CSV. Integrate with anything you already use." },
          ].map((f, i) => (
            <div key={i} style={{
              background: C.cream, borderRadius: 12, padding: 24,
              border: `1px solid ${C.line}`,
            }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.navy, marginBottom: 8 }}>{f.t}</div>
              <div style={{ fontSize: 13, color: C.text2, lineHeight: 1.65 }}>{f.d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PillarTrust = () => {
  const { mob } = useW();
  return (
    <div style={{
      background: C.cream, padding: mob ? "80px 24px" : "120px 40px",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid",
        gridTemplateColumns: mob ? "1fr" : "1fr 1fr", gap: 64, alignItems: "center",
      }}>
        <div>
          <div style={{ fontSize: 11, color: C.red, letterSpacing: 2.5, fontWeight: 700, marginBottom: 16 }}>
            TRUST
          </div>
          <h2 style={{ fontSize: mob ? 36 : 52, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -1.5, lineHeight: 1.05 }}>
            Built to be the<br/>last courier you<br/>ever change.
          </h2>
          <p style={{ fontSize: mob ? 16 : 18, color: C.text2, marginTop: 24, lineHeight: 1.7 }}>
            Compliance-ready records. Audit-grade reconciliation. Standard insurance.
            Built so banks, government departments, and large enterprises can use {BRAND}
            with their compliance teams' approval.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {[
            { n: "₹50K", l: "Insured per shipment", sub: "Standard on Express" },
            { n: "T+3", l: "COD to bank", sub: "Automated settlement" },
            { n: "100%", l: "GST compliant", sub: "Audit-ready invoicing" },
            { n: "24×7", l: "Tracking + support", sub: "Voice and WhatsApp" },
          ].map((s, i) => (
            <div key={i} style={{
              background: C.white, borderRadius: 12, padding: 24,
              border: `1px solid ${C.line}`,
            }}>
              <div style={{ fontSize: mob ? 30 : 36, fontWeight: 800, color: C.navy, letterSpacing: -1, lineHeight: 1 }}>{s.n}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginTop: 10 }}>{s.l}</div>
              <div style={{ fontSize: 11, color: C.text3, marginTop: 4 }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// SERVICES — Single statement of capability, six tags
// ═══════════════════════════════════════════════════════════════
const Services = () => {
  const { mob } = useW();
  const items = [
    { title: "Express", tagline: "When speed matters", desc: "24-hour delivery between major cities. 2-3 days nationwide. Insurance up to ₹1 lakh." },
    { title: "Standard", tagline: "Most economical", desc: "Pan-India parcel delivery in 4-7 days at our lowest rates. Up to 35 kg per parcel." },
    { title: "Documents", tagline: "Court-admissible", desc: "Registered delivery with proof of dispatch and receipt. Suitable for legal and statutory filings." },
    { title: "International", tagline: "190+ countries", desc: "Express international shipping with end-to-end tracking. CN23 customs forms auto-generated." },
    { title: "COD", tagline: "Cash on delivery", desc: "We collect, settle to your bank in T+3 days. Article-level reconciliation included." },
    { title: "Bulk", tagline: "Volume customers", desc: "Multi-branch accounts, consolidated billing, GSTR-1 ready exports, dedicated managers." },
  ];
  return (
    <div id="services" style={{ background: C.white, padding: mob ? "80px 24px" : "120px 40px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ marginBottom: 56, maxWidth: 720, textAlign: mob ? "left" : "center", margin: mob ? "0 0 56px" : "0 auto 56px" }}>
          <div style={{ fontSize: 11, color: C.red, letterSpacing: 2.5, fontWeight: 700, marginBottom: 16 }}>
            EVERY KIND OF SHIPPING
          </div>
          <h2 style={{ fontSize: mob ? 36 : 52, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -1.5, lineHeight: 1.05 }}>
            Every kind of shipment<br/>your business needs.
          </h2>
          <p style={{ fontSize: mob ? 16 : 18, color: C.text2, marginTop: 24, lineHeight: 1.7 }}>
            Documents. Parcels. Express. Standard. International. COD. Bulk.
            Whatever you ship, we have a service for it. All on one account.
          </p>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: mob ? "1fr" : "repeat(3, 1fr)",
          gap: 14,
        }}>
          {items.map((it, i) => (
            <div key={i} style={{
              background: C.cream, borderRadius: 12, padding: 28,
              border: `1px solid ${C.line}`, transition: "all 0.2s",
            }}
              onMouseOver={(e) => { e.currentTarget.style.borderColor = C.red; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseOut={(e) => { e.currentTarget.style.borderColor = C.line; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <div style={{ fontSize: 22, fontWeight: 800, color: C.navy, letterSpacing: -0.3, marginBottom: 4 }}>{it.title}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.red, marginBottom: 14, letterSpacing: 0.3 }}>{it.tagline}</div>
              <div style={{ fontSize: 14, color: C.text2, lineHeight: 1.7 }}>{it.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// THE FINALLY MOMENT — Voice support + WhatsApp + 24h
// ═══════════════════════════════════════════════════════════════
const FinallyMoment = () => {
  const { mob } = useW();
  return (
    <div style={{
      background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyDeep} 100%)`,
      padding: mob ? "80px 24px" : "120px 40px",
      color: C.white, position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: "20%", right: "-15%", width: 600, height: 600,
        background: `radial-gradient(circle, ${C.amber}15 0%, transparent 60%)`,
        pointerEvents: "none",
      }} />
      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative" }}>
        <div style={{ maxWidth: 720, marginBottom: 56 }}>
          <div style={{ fontSize: 11, color: C.amber, letterSpacing: 2.5, fontWeight: 700, marginBottom: 16 }}>
            THE THINGS YOU'VE BEEN ASKING FOR
          </div>
          <h2 style={{ fontSize: mob ? 36 : 52, fontWeight: 800, margin: 0, letterSpacing: -1.5, lineHeight: 1.05 }}>
            Finally, a courier<br/>that picks up the phone.
          </h2>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: mob ? "1fr" : "repeat(3, 1fr)",
          gap: 14,
        }}>
          {[
            {
              t: "Voice support, 24×7",
              d: "Real humans answer your calls. No ticket queues, no chatbot mazes, no language barriers. Help in Hindi, English, and seven regional languages.",
            },
            {
              t: "WhatsApp ordering",
              d: "Book a shipment, get a label, track delivery — all from WhatsApp. The way India already does business.",
            },
            {
              t: "24-hour delivery",
              d: "Express service between major cities delivers in 24 hours. Document and parcel — same speed, both ways.",
            },
          ].map((f, i) => (
            <div key={i} style={{
              background: "#FFFFFF08", borderRadius: 12, padding: 28,
              border: "1px solid #FFFFFF15", backdropFilter: "blur(10px)",
            }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.amber, marginBottom: 12 }}>{f.t}</div>
              <div style={{ fontSize: 14, color: "#FFFFFFCC", lineHeight: 1.7 }}>{f.d}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// ENTERPRISE — name the actual pain
// ═══════════════════════════════════════════════════════════════
const Enterprise = () => {
  const { mob } = useW();
  return (
    <div id="enterprise" style={{
      background: C.white, padding: mob ? "80px 24px" : "120px 40px",
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ marginBottom: 56, maxWidth: 720 }}>
          <div style={{ fontSize: 11, color: C.red, letterSpacing: 2.5, fontWeight: 700, marginBottom: 16 }}>
            FOR ENTERPRISES
          </div>
          <h2 style={{ fontSize: mob ? 36 : 52, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -1.5, lineHeight: 1.05 }}>
            We solve the things<br/>that quietly cost you<br/>millions every year.
          </h2>
          <p style={{ fontSize: mob ? 16 : 18, color: C.text2, marginTop: 24, lineHeight: 1.7 }}>
            Banks, insurers, retailers, and government departments — the operational pain
            of shipping at scale isn't usually the shipping itself. It's the back office.
            We rebuilt the back office.
          </p>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: mob ? "1fr" : "repeat(2, 1fr)",
          gap: 14,
        }}>
          {[
            {
              t: "Reconciliation, automated",
              d: "Article-level matching of every shipment against carrier invoices. End the manual matching that takes your accounts team three days every month.",
            },
            {
              t: "Consolidated multi-branch billing",
              d: "Hundreds of branches. One bill. One dashboard. One reconciliation. Branch-level wallets with head-office consolidation, exactly the way enterprise finance teams need it.",
            },
            {
              t: "GSTR-1 ready exports",
              d: "Tax invoices generated per shipment. Monthly statements bundled and ready for direct upload to your GST returns. Your auditor will not have a single question.",
            },
            {
              t: "Custom SLAs and rate slabs",
              d: "Volume-based custom contracts. Defined SLAs for pickup, transit, and delivery. Penalty clauses included. Dedicated account manager and priority support.",
            },
            {
              t: "Sovereign deployment",
              d: "For regulated industries — banks, insurers, government — we offer dedicated deployment within your security perimeter. Your data does not leave your premises.",
            },
            {
              t: "White-label and API-first",
              d: "Full REST APIs for system integration. White-label tracking pages with your branding. Bulk processing pipelines for shipping volumes that exceed manual workflows.",
            },
          ].map((f, i) => (
            <div key={i} style={{
              background: C.cream, borderRadius: 12, padding: 28,
              border: `1px solid ${C.line}`,
            }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.navy, marginBottom: 12 }}>{f.t}</div>
              <div style={{ fontSize: 14, color: C.text2, lineHeight: 1.7 }}>{f.d}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 40, textAlign: mob ? "left" : "center" }}>
          <a href="mailto:enterprise@shiftswift.in" style={{
            display: "inline-block", padding: "16px 32px",
            background: C.navy, color: C.white, borderRadius: 8,
            textDecoration: "none", fontSize: 15, fontWeight: 700,
          }}>
            Talk to enterprise sales →
          </a>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// INTEGRATIONS BELT
// ═══════════════════════════════════════════════════════════════
const Integrations = () => {
  const { mob } = useW();
  return (
    <div style={{ background: C.cream, padding: mob ? "60px 24px" : "80px 40px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 11, color: C.text3, letterSpacing: 2.5, fontWeight: 700, marginBottom: 24 }}>
          CONNECTS TO YOUR EXISTING STACK
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: mob ? "repeat(3, 1fr)" : "repeat(6, 1fr)",
          gap: 14, alignItems: "center",
        }}>
          {["Shopify", "WooCommerce", "Amazon", "Meesho", "ONDC", "Razorpay"].map((name) => (
            <div key={name} style={{
              background: C.white, borderRadius: 10, padding: "22px 12px",
              border: `1px solid ${C.line}`, fontSize: 13, fontWeight: 700, color: C.navy,
            }}>
              {name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// FAQ
// ═══════════════════════════════════════════════════════════════
const FAQ = () => {
  const { mob } = useW();
  const [open, setOpen] = useState(0);
  const faqs = [
    [`What is ${BRAND}?`,
     `${BRAND} is a courier company built for businesses. We deliver documents and parcels across India and to 190+ countries — with a modern dashboard, real-time tracking, automated reconciliation, voice support 24×7, WhatsApp ordering, and the lowest rates in the country.`],
    ["How can your rates be lower than the market?",
     "Our scale and operational model lets us offer better economics. The more our customers ship, the better the rates get — automatically, with no haggling and no contracts."],
    ["What's your delivery network?",
     "Every PIN code in India. 190+ countries internationally. Express delivery between metros in 24 hours. Standard delivery 3-7 days nationwide."],
    ["What about COD orders?",
     "We collect, we settle. Cash to your bank account in T+3 days. Article-level reconciliation included. No follow-ups."],
    ["Can I integrate with my online store?",
     "Yes. One-click OAuth for Shopify, WooCommerce, Amazon, Meesho. Orders pull automatically. Status updates push back. Buyers get tracking notifications."],
    ["Is there a minimum volume?",
     "No. Ship 1 parcel a month or 100,000 — same platform, same rates. Volume slabs apply automatically as you grow."],
    ["What happens if a parcel is lost or damaged?",
     "Built-in dispute system. 72-hour filing window. Standard insurance up to ₹50,000 on Express service. Resolution typically within 7-14 working days."],
    ["Do you really pick up the phone?",
     "Yes. Voice support 24×7 in Hindi, English, and seven regional languages. No ticket queues. No chatbot mazes."],
  ];
  return (
    <div style={{ background: C.white, padding: mob ? "80px 24px" : "120px 40px" }}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <h2 style={{ fontSize: mob ? 32 : 44, fontWeight: 800, color: C.navy, textAlign: "center", margin: 0, marginBottom: 48, letterSpacing: -1 }}>
          Common questions
        </h2>
        {faqs.map(([q, a], i) => (
          <div key={i} style={{
            background: C.cream, borderRadius: 10, marginBottom: 10,
            border: `1px solid ${C.line}`,
          }}>
            <button onClick={() => setOpen(open === i ? -1 : i)} style={{
              width: "100%", padding: "20px 24px", background: "transparent",
              border: "none", textAlign: "left", cursor: "pointer",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              fontSize: 15, fontWeight: 700, color: C.navy, fontFamily: "inherit",
            }}>
              <span>{q}</span>
              <span style={{ color: C.red, fontSize: 24, lineHeight: 1, marginLeft: 16 }}>{open === i ? "−" : "+"}</span>
            </button>
            {open === i && (
              <div style={{ padding: "0 24px 24px", color: C.text2, fontSize: 14, lineHeight: 1.75 }}>{a}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// CLOSE — Join the businesses that are switching
// ═══════════════════════════════════════════════════════════════
const Close = () => {
  const { mob } = useW();
  return (
    <div style={{
      background: C.navy, padding: mob ? "80px 24px" : "140px 40px",
      textAlign: "center", color: C.white, position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(circle at center, ${C.saffron}20 0%, transparent 60%)`,
        pointerEvents: "none",
      }} />
      <div style={{ position: "relative", maxWidth: 720, margin: "0 auto" }}>
        <h2 style={{ fontSize: mob ? 40 : 64, fontWeight: 800, margin: 0, letterSpacing: -2, lineHeight: 1.05 }}>
          Ship forward.
        </h2>
        <p style={{ fontSize: mob ? 17 : 20, color: "#FFFFFFCC", maxWidth: 540, margin: "24px auto 40px", lineHeight: 1.6 }}>
          60-second sign-up. Pay-as-you-ship. The best rates in the country, on day one.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/join" style={{
            display: "inline-block", background: C.red, color: C.white,
            padding: "18px 44px", borderRadius: 10,
            textDecoration: "none", fontSize: 17, fontWeight: 800,
            boxShadow: "0 6px 24px rgba(200,48,42,0.45)",
          }}>Open your account →</Link>
          <a href="mailto:enterprise@shiftswift.in" style={{
            display: "inline-block", background: "transparent", color: C.white,
            padding: "16px 36px", borderRadius: 10,
            textDecoration: "none", fontSize: 16, fontWeight: 700,
            border: "2px solid #FFFFFF40",
          }}>Enterprise inquiry</a>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// FOOTER
// ═══════════════════════════════════════════════════════════════
const Footer = () => {
  const { mob } = useW();
  return (
    <footer style={{ background: C.navyDeep, color: "#FFFFFF99", padding: mob ? "40px 24px" : "60px 40px 30px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: mob ? "1fr 1fr" : "2fr 1fr 1fr 1fr 1fr",
          gap: 32, marginBottom: 36,
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{
                width: 32, height: 32, background: C.red, borderRadius: 8,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: C.white, fontWeight: 800, fontSize: 16, letterSpacing: -1,
              }}>S</div>
              <div style={{ fontSize: 19, fontWeight: 800, color: C.white }}>{BRAND}</div>
            </div>
            <div style={{ fontSize: 13, color: "#FFFFFF80", lineHeight: 1.7 }}>
              The courier India was waiting for.<br />
              Operated by Aarama Ventures LLP.
            </div>
          </div>
          <FooterCol title="Services" links={["Express", "Standard", "Documents", "International", "COD", "Bulk"]} />
          <FooterCol title="Platform" links={["Rate Calculator", "Track Shipment", "WhatsApp", "API Docs", "Integrations"]} />
          <FooterCol title="Company" links={["About", "Enterprise", "Careers", "Press", "Contact"]} />
          <FooterCol title="Legal" links={["Terms", "Privacy", "Refund", "Acceptable Use", "Compliance"]} />
        </div>
        <div style={{
          paddingTop: 24, borderTop: "1px solid #FFFFFF12",
          fontSize: 12, color: "#FFFFFF60", textAlign: "center",
        }}>
          © 2026 Aarama Ventures LLP. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <div style={{ fontSize: 11, color: C.amber, letterSpacing: 1.5, fontWeight: 700, marginBottom: 14 }}>{title.toUpperCase()}</div>
    {links.map((l) => (
      <div key={l} style={{ marginBottom: 10 }}>
        <a href="#" style={{ color: "#FFFFFF99", textDecoration: "none", fontSize: 13 }}>{l}</a>
      </div>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════
export default function ShiftswiftLanding() {
  return (
    <div style={{ background: C.cream, minHeight: "100vh", fontFamily: "-apple-system, system-ui, Inter, sans-serif", color: C.text }}>
      <Nav />
      <Hero />
      <CalculatorBand />
      <SoulLine />
      <PillarReach />
      <PillarPrice />
      <PillarTechnology />
      <PillarTrust />
      <Services />
      <FinallyMoment />
      <Enterprise />
      <Integrations />
      <FAQ />
      <Close />
      <Footer />
    </div>
  );
}
